import React from 'react';
import { motion } from 'framer-motion';
import { FileText } from 'lucide-react';

export function Terms() {
  return (
    <div className="min-h-screen pt-20 pb-12 bg-earth-900">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl mx-auto"
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="h-12 w-12 rounded-full bg-sand-400/10 flex items-center justify-center">
              <FileText className="h-6 w-6 text-sand-400" />
            </div>
            <div>
              <h1 className="text-3xl font-display">Terms of Service</h1>
              <p className="text-sand-400">Last Updated: April 2025</p>
            </div>
          </div>

          <div className="prose prose-invert prose-sand max-w-none">
            <p className="text-sand-300">
              These Terms of Service ("Terms") apply to your access to and use of the websites and other online products and services (collectively, our "Services") provided by Rendezvous Inc. ("Rendezvous," "Company," "we," or "us"). Rendezvous Inc. is a Delaware corporation.
            </p>

            <p className="text-sand-300">
              By accessing or using our Services, you agree to be bound by these Terms. If you do not agree to these Terms in their entirety, do not use our Services.
            </p>

            <p className="text-sand-300">
              We may modify these Terms from time to time. If we make changes, we will provide notice by updating the date at the top of the Terms and may provide additional notice, such as through a banner on our homepage. Your continued use of our Services will confirm your acceptance of the revised Terms. If you do not agree to the amended Terms, you must stop using our Services.
            </p>

            <p className="text-sand-300">
              If you have any questions about these Terms or our Services, please contact us at info@letsrendezvous.co.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">1. Privacy</h2>
            <p className="text-sand-300">
              Please review our <Link to="/privacy" className="text-sand-100 hover:underline">Privacy Policy</Link> to understand how we collect, use, and disclose information about you.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">2. Eligibility</h2>
            <p className="text-sand-300">
              You must have the legal capacity to enter into a binding contract and be at least 18 years old (or the age of legal majority in your jurisdiction) to use our Services. If you use our Services on behalf of an entity, you represent that you are authorized to accept these Terms on behalf of the entity. You may not use our Services if you are located in a jurisdiction embargoed by the United States or if such use is illegal under applicable law.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">3. Marketplace Nature of Services</h2>
            <p className="text-sand-300">
              Rendezvous is a curated digital marketplace facilitating the promotion and discovery of third-party experiences. We may, from time to time, list experiences that we operate ourselves ("1P Experiences"), but unless explicitly stated otherwise, all experiences are designed, operated, and delivered by third-party creators ("Creators").
            </p>
            <p className="text-sand-300">
              We do not produce, organize, or deliver the experiences listed on our platform unless explicitly marked as a Rendezvous-operated experience. The legal contract for any experience is between you and the Creator. Rendezvous is not a party to that agreement.
            </p>
            <p className="text-sand-300">
              We may facilitate payment processing on behalf of Creators, but such facilitation does not constitute Rendezvous as a party to any transaction. The Creator is solely responsible for honoring any bookings, delivering the experience as described, and addressing any issues that may arise.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">4. No Liability for Third-Party Experiences</h2>
            <p className="text-sand-300">
              Rendezvous disclaims all liability for any act, omission, breach, or failure to perform by any Creator. We are not liable for any damage, loss, disappointment, or other issues arising from the provision (or lack thereof) of any experience.
            </p>
            <p className="text-sand-300">
              All claims related to unsatisfactory experiences, cancellations, delays, or any other failure of delivery must be directed solely to the Creator. Each experience listing will identify the relevant Creator and provide their contact information for support or redress.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">5. Limited Payment Collection Agent</h2>
            <p className="text-sand-300">
              You acknowledge and agree that we may, in certain cases, act as a limited payment collection agent solely for the purpose of accepting and processing payments from you on behalf of the Creator. Your payment to Rendezvous shall be deemed equivalent to a direct payment made to the Creator, and the Creator will fulfill their obligations to you as if they had received the payment directly.
            </p>
            <p className="text-sand-300">
              Rendezvous' role as a limited payment collection agent is strictly administrative. Rendezvous does not own, control, or operate any experiences listed by Creators (unless expressly stated otherwise), nor does it guarantee or assume responsibility for the performance, quality, safety, legality, or timely delivery of any such experience or service.
            </p>
            <p className="text-sand-300">
              Payments may be processed using third-party payment providers or financial institutions, including across borders. Where applicable, you authorize Rendezvous to convert currencies and remit funds to Creators in their local currencies, less applicable service or transaction fees. You are responsible for any international transaction fees, currency exchange charges, or applicable taxes.
            </p>
            <p className="text-sand-300">
              To the maximum extent permitted by applicable law, you agree that Rendezvous is not liable for any acts or omissions of any Creator, including cancellations, dissatisfaction with the experience, or failure to deliver the services as described. Any requests for refunds, chargebacks, or redress must be made directly to the Creator. Rendezvous may, at its discretion and without obligation, offer to mediate such disputes on a best-efforts basis but shall not be liable if the Creator does not cooperate or agree to a resolution.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">6. Refunds and Disputes</h2>
            <p className="text-sand-300">
              Rendezvous does not guarantee or process refunds for experiences. All refund and cancellation policies are determined by the Creator and will be made available in the experience listing.
            </p>
            <p className="text-sand-300">
              You agree to first contact the Creator directly to resolve any dispute or request a refund. Rendezvous may, at its sole discretion and on a good faith, best-efforts basis, assist in mediating such claims but is under no obligation to do so and will not be liable for the Creator's actions or inactions.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">7. Payment Processing and Third-Party Platforms</h2>
            <p className="text-sand-300">
              Some transactions may occur directly on the Rendezvous platform. Others may redirect you to third-party payment or ticketing platforms. In both cases, the transaction is solely between you and the Creator. Rendezvous does not assume any responsibility or liability arising from these transactions.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">8. User Accounts and Security</h2>
            <p className="text-sand-300">
              You may be required to create an account to access some Services. You must provide accurate information and maintain the security of your account. You are responsible for all activities under your account.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">9. User Content</h2>
            <p className="text-sand-300">
              You retain ownership of content you post but grant us a perpetual, irrevocable, sublicensable, nonexclusive, royalty-free license to use it across all media. You must have rights to the content you upload and not infringe others' rights. We may remove content at our discretion.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">10. Prohibited Conduct and Content</h2>
            <p className="text-sand-300">
              You agree not to misuse the Services, post harmful or illegal content, or interfere with other users. This includes but is not limited to impersonation, harassment, reverse engineering, spam, unauthorized access, and intellectual property infringement. You may only post content that is non-confidential and that you have the right to share.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">11. Ownership and Limited License</h2>
            <p className="text-sand-300">
              The Services and content therein are owned by Rendezvous or our licensors. We grant you a limited, revocable license to use them for personal, non-commercial use.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">12. Trademarks</h2>
            <p className="text-sand-300">
              All trademarks, logos, and service marks displayed on the Services are our property or the property of others. You may not use them without permission.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">13. Feedback</h2>
            <p className="text-sand-300">
              If you send us suggestions or ideas, you grant us the right to use them without restriction or compensation. You waive any moral rights or rights of publicity related to such Feedback.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">14. Repeat Infringer Policy and Copyright Complaints</h2>
            <p className="text-sand-300">
              We may terminate users who repeatedly infringe copyrights. If you believe your work is infringed, contact our designated agent:
            </p>
            <p className="text-sand-300">
              Designated Agent: Legal Department
              <br />
              Email: info@letsrendezvous.co
            </p>
            <p className="text-sand-300">
              Please see 17 U.S.C. § 512(c)(3) for requirements.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">15. Third-Party Content</h2>
            <p className="text-sand-300">
              Our Services may display third-party content or links. We are not responsible for such content and do not endorse it. Your interactions with third parties are solely between you and them.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">16. Disclaimers</h2>
            <p className="text-sand-300">
              The Services are provided "as is." Rendezvous disclaims all warranties, including merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Services will be secure, error-free, or available at all times.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">17. Limitation of Liability</h2>
            <p className="text-sand-300">
              To the fullest extent permitted by law, Rendezvous and its affiliates are not liable for indirect, incidental, special, punitive, or consequential damages, or lost profits.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">18. User Content Releases</h2>
            <p className="text-sand-300">
              You waive any moral rights and release Rendezvous from any claims related to the use of your submitted content, including claims of infringement or defamation.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">19. Data Transfer and Processing</h2>
            <p className="text-sand-300">
              By using the Services, you consent to the transfer and processing of your data in the U.S. and other jurisdictions.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">20. Governing Law and Venue</h2>
            <p className="text-sand-300">
              These Terms are governed by the laws of Delaware. Any disputes shall be resolved in the state or federal courts in Delaware.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">21. Modifying or Terminating the Services</h2>
            <p className="text-sand-300">
              We may modify, suspend, or discontinue the Services at any time. You may stop using the Services at any time.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">22. Severability</h2>
            <p className="text-sand-300">
              If any provision is found unenforceable, the remaining provisions will remain in effect.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">23. Miscellaneous</h2>
            <p className="text-sand-300">
              These Terms constitute the entire agreement between you and Rendezvous. Section titles are for convenience only. You may not assign your rights without our consent. We may assign these Terms without restriction.
            </p>

            <p className="text-sand-300 mt-8">
              For questions or concerns, contact: info@letsrendezvous.co
            </p>
            
            <p className="text-sand-300 mt-4">
              Legal & Registered Agent Address:
              <br />
              Corporation Service Company (Registered Agent)
              <br />
              251 Little Falls Drive
              <br />
              Wilmington, DE 19808
              <br />
              New Castle County, USA
            </p>
            
            <p className="text-sand-300 mt-4">
              Please note: This is the registered agent address for legal correspondence and service of process. It is not a customer support location.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// Add the Link component
import { Link } from 'react-router-dom';